import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import SearchBar from '@/components/SearchBar';
import FilterDropdown from '@/components/FilterDropdown';
import { InventoryFormModal } from '@/components/InventoryFormModal';
import { StockAdjustmentModal } from '@/components/StockAdjustmentModal';
import { useInventoryWebSocket } from '@/hooks/useWebSocket';
import type { InventoryItem, Restaurant } from '@/types/api';
import { Package, Plus, AlertTriangle } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';

const PAGE_SIZE = 50;

export default function InventoryPage() {
  const canCreate = useHasPermission(PERM.INVENTORY_CREATE);
  const canUpdate = useHasPermission(PERM.INVENTORY_UPDATE);

  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null);

  const queryClient = useQueryClient();

  // Debounce search input before it drives a server request.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Any filter change invalidates the current page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, categoryFilter, showLowStockOnly, selectedRestaurantId]);

  const { data: restaurantsData } = useQuery({
    queryKey: ['restaurants'],
    queryFn: () => apiClient.getRestaurants(0, 100),
  });

  const restaurants = restaurantsData?.content || [];

  useInventoryWebSocket(
    selectedRestaurantId?.toString() || null,
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
    }, [queryClient])
  );

  const { data: inventoryData, isLoading } = useQuery({
    queryKey: ['inventory', selectedRestaurantId, debouncedSearch, categoryFilter, showLowStockOnly, page],
    queryFn: () =>
      apiClient.getInventoryItems(selectedRestaurantId!, {
        search: debouncedSearch || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        lowStock: showLowStockOnly || undefined,
        page,
        size: PAGE_SIZE,
      }),
    enabled: !!selectedRestaurantId,
  });

  const inventoryItems = inventoryData?.content || [];
  const totalPages = inventoryData?.totalPages ?? 0;
  const totalElements = inventoryData?.totalElements ?? 0;

  const { data: alerts } = useQuery({
    queryKey: ['inventory-alerts', selectedRestaurantId],
    queryFn: () => apiClient.getLowStockAlerts(selectedRestaurantId!),
    enabled: !!selectedRestaurantId,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories', selectedRestaurantId],
    queryFn: () => apiClient.getInventoryCategories(selectedRestaurantId!),
    enabled: !!selectedRestaurantId,
  });

  const categories = categoriesData || [];

  // Server already applies search/category/lowStock filters (C1 fix) — render the page as-is.
  const filteredItems = inventoryItems;

  const lowStockCount = useMemo(
    () => alerts?.filter((alert) => alert.type === 'LOW_STOCK').length ?? 0,
    [alerts]
  );

  const getStockStatus = (item: InventoryItem) => {
    if (item.quantity <= item.minThreshold) {
      return 'low';
    }
    if (item.quantity >= item.maxThreshold) {
      return 'overstock';
    }
    return 'normal';
  };

  const getStockBadgeClass = (status: string) => {
    switch (status) {
      case 'low':
        return 'badge-danger';
      case 'overstock':
        return 'badge-warning';
      default:
        return 'badge-success';
    }
  };

  const getStockLabel = (status: string) => {
    switch (status) {
      case 'low':
        return 'Low Stock';
      case 'overstock':
        return 'Overstock';
      default:
        return 'Normal';
    }
  };

  // Row cells double as the edit-opener; only wire it up when the user can actually update.
  const handleRowClick = (item: InventoryItem) => {
    if (canUpdate) {
      setEditingItem(item);
    }
  };

  if (!selectedRestaurantId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage restaurant inventory
          </p>
        </div>

        <div className="card p-4">
          <FilterDropdown
            label="Select Restaurant"
            value={selectedRestaurantId?.toString() || ''}
            onChange={(value) => setSelectedRestaurantId(value ? parseInt(value) : null)}
            options={[
              { label: 'Select a restaurant...', value: '' },
              ...restaurants.map((r: Restaurant) => ({
                label: r.name,
                value: r.id.toString(),
              })),
            ]}
          />
        </div>

        <div className="card p-12 text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No restaurant selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select a restaurant to view inventory
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and manage restaurant inventory
          </p>
        </div>

        {canCreate && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn btn-primary h-10"
            disabled={!selectedRestaurantId}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </button>
        )}
      </div>

      <div className="card p-4">
        <FilterDropdown
          label="Restaurant"
          value={selectedRestaurantId?.toString() || ''}
          onChange={(value) => setSelectedRestaurantId(value ? parseInt(value) : null)}
          options={[
            { label: 'Select a restaurant...', value: '' },
            ...restaurants.map((r: Restaurant) => ({
              label: r.name,
              value: r.id.toString(),
            })),
          ]}
        />
      </div>

      {lowStockCount > 0 && (
        <div className="card bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Low Stock Alert:</span> {lowStockCount} item
                {lowStockCount !== 1 ? 's' : ''} running low on stock
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-1">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search items..."
            />
          </div>
          <FilterDropdown
            label="Category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[
              { label: 'All Categories', value: 'all' },
              ...categories.map((cat) => ({
                label: cat.name,
                value: cat.name,
              })),
            ]}
          />
          <div className="flex items-end">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Low Stock Only</span>
            </label>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          Showing {filteredItems.length} of {totalElements} items
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading inventory...</div>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost/Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item: InventoryItem) => {
                    const status = getStockStatus(item);
                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50"
                      >
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {item.name}
                          </div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm text-gray-500">{item.sku}</div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm text-gray-500">{item.category}</div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm text-gray-900">{item.quantity}</div>
                          <div className="text-xs text-gray-500">
                            Min: {item.minThreshold} / Max: {item.maxThreshold}
                          </div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm text-gray-500">{item.unit}</div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <div className="text-sm text-gray-900">
                            ${item.costPerUnit.toFixed(2)}
                          </div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap ${canUpdate ? 'cursor-pointer' : ''}`}
                          onClick={() => handleRowClick(item)}
                        >
                          <span className={`badge ${getStockBadgeClass(status)}`}>
                            {getStockLabel(status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {canUpdate && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAdjustingItem(item);
                              }}
                              className="btn btn-outline btn-sm mr-2"
                            >
                              Adjust Stock
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {filteredItems.length === 0 && (
            <div className="card p-12 text-center">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No items found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery || categoryFilter !== 'all' || showLowStockOnly
                  ? 'No inventory items match your filters'
                  : 'No inventory items found for this restaurant'}
              </p>
              {(searchQuery || categoryFilter !== 'all' || showLowStockOnly) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setShowLowStockOnly(false);
                  }}
                  className="btn btn-outline mt-4 h-10"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </>
      )}

      <InventoryFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        restaurantId={selectedRestaurantId!}
        categories={categories}
      />

      <InventoryFormModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        restaurantId={selectedRestaurantId!}
        item={editingItem}
        categories={categories}
      />

      <StockAdjustmentModal
        isOpen={!!adjustingItem}
        onClose={() => setAdjustingItem(null)}
        restaurantId={selectedRestaurantId!}
        item={adjustingItem}
      />
    </div>
  );
}
